/**
 * 🔺 TURBO-CAT™ FORMAT PYRAMID v3.1.1
 *
 * ROW 20 IN PROGRESS - 199 FORMATS CATALOGUED!
 *
 * Like precious metals in a catalytic converter - each format triggers
 * a specific intelligence reaction. We CAN catalyze ANY format, but only
 * the WORTHY earn a pyramid stone!
 *
 * Pyramid Evolution:
 * ├── Row 17: Sum(1..17) + 1 = 154 (THE SACRED - ACHIEVED 2024)
 * ├── Row 18: Sum(1..18) + 1 = 172 (BEYOND SACRED - ACHIEVED)
 * ├── Row 19: Sum(1..19) + 1 = 190 (ACHIEVED 2025-12-17)
 * └── Row 20: Sum(1..20) + 1 = 211 (IN PROGRESS - 9/21 added)
 *
 * Row 20 Tier 1 Added (2025-12-17):
 * - build.zig.zon (Zig packages)
 * - gleam.toml (Gleam - 2nd most admired 2025)
 * - bunfig.toml (Bun config)
 * - mise.toml / .mise.toml (mise version manager)
 * - manifest.toml (Flox/Nix environments)
 * - justfile (Just command runner - 10k+ stars)
 * - .pre-commit-config.yaml (Git hooks framework)
 * - CLAUDE.md (Claude Code AI context)
 *
 * THE DOCTRINE: To add a new format, PROVE it's better than existing ones!
 *
 * 😽 We are the FORMAT FREAKS - Quality over Quantity!
 * 🏆 199 formats - CHAMPIONSHIP GRADE FORMAT DETECTION
 */

/**
 * A slot value here must name what the file itself proves, in a slot that
 * value fills: `vercel.json` → hosting Vercel; `Dockerfile` → no hosting (it
 * shows a container build, not where the app runs); `jest.config.js` →
 * nothing for CI/CD (Jest is a test runner, not a pipeline). No generic words ('Cloud',
 * 'Containerized', 'API Server'), no language in a framework or backend slot,
 * and no `runtime: Node.js` from a framework config alone — package.json is
 * that fact, and detectRuntime reads it. `faf auto` writes these values into
 * project.faf, so a value that is not a fact about the repo is a guess.
 */
export interface FormatKnowledge {
  frameworks: string[];           // Possible frameworks this format indicates
  slots: Partial<ContextSlots>;  // Direct Context-On-Demand slot mappings
  priority: number;               // Intelligence score (0-35 points)
  intelligence: 'ultra-high' | 'high' | 'medium' | 'low';
  confirmWith?: string[];         // Additional files to check for confirmation
}

export interface ContextSlots {
  // Technical slots (1-15) - camelCase versions
  framework: string;
  mainLanguage: string;
  buildTool: string;
  packageManager: string;
  hosting: string;
  backend: string;
  apiType: string;
  cicd: string;
  database: string;
  cssFramework: string;
  uiLibrary: string;
  stateManagement: string;
  server: string;
  connection: string;
  runtime: string;
  // Display slots (for stack display compatibility)
  build: string;

  // snake_case versions (for YAML compatibility)
  main_language: string;
  build_tool: string;
  pkg_manager: string;
  api: string;
  css: string;
  ui_library: string;
  state: string;
  db: string;

  // Human context slots (16-21)
  targetUser: string;
  coreProblem: string;
  missionPurpose: string;
  deploymentMarket: string;
  timeline: string;
  approach: string;
}

/**
 * THE KNOWLEDGE BASE
 * 
 * Add any file format here and map it to:
 * 1. Frameworks it indicates
 * 2. Context slots it can fill
 * 3. Priority/intelligence score
 * 
 * The fab-formats engine will automatically use this knowledge!
 */
export const KNOWLEDGE_BASE: Record<string, FormatKnowledge> = {
  // ============================================
  // ULTRA-HIGH VALUE (35 points) - Package Managers
  // ============================================
  
  'package.json': {
    frameworks: ['Node.js', 'JavaScript', 'TypeScript'],
    slots: {
      packageManager: 'npm/yarn/pnpm',
      mainLanguage: 'JavaScript/TypeScript'
    },
    priority: 35,
    intelligence: 'ultra-high'
  },

  'README.md': {
    frameworks: [], // Can be any framework
    // No slot values: a README's who/what/why is read from its TEXT by the
    // Relentless extractor. Constant guesses here ('developers', …) were
    // asserted for every repo with a README — sourced-only forbids that.
    slots: {},
    priority: 25, // HIGH: Human context goldmine
    intelligence: 'high'
  },

  'requirements.txt': {
    frameworks: ['Python'],
    slots: { 
      packageManager: 'pip',
      mainLanguage: 'Python'
    },
    priority: 35,
    intelligence: 'ultra-high'
  },
  
  'Pipfile': {
    frameworks: ['Python'],
    slots: { 
      packageManager: 'pipenv',
      mainLanguage: 'Python'
    },
    priority: 35,
    intelligence: 'ultra-high'
  },
  
  'pyproject.toml': {
    frameworks: ['Python'],
    slots: {
      mainLanguage: 'Python',
      runtime: 'Python'
    },
    priority: 36,
    intelligence: 'ultra-high'
  },
  
  // Content-aware via detectRubyProject in turbo-cat.ts — Gemfile alone ≠ Rails.
  'Gemfile': {
    frameworks: ['Ruby'],
    slots: {
      packageManager: 'bundler',
      mainLanguage: 'Ruby'
    },
    priority: 35,
    intelligence: 'ultra-high'
  },
  'Gemfile.lock': {
    frameworks: ['Ruby'],
    slots: {
      packageManager: 'bundler',
      mainLanguage: 'Ruby'
    },
    priority: 34,
    intelligence: 'high'
  },
  
  'composer.json': {
    frameworks: ['PHP', 'Laravel', 'Symfony'],
    slots: { 
      packageManager: 'composer',
      mainLanguage: 'PHP'
    },
    priority: 35,
    intelligence: 'ultra-high'
  },
  
  'Cargo.toml': {
    frameworks: ['Rust'],
    slots: {
      packageManager: 'cargo',
      mainLanguage: 'Rust',
      buildTool: 'cargo'
    },
    priority: 36,
    intelligence: 'ultra-high'
  },

  'go.mod': {
    // Filename-only FALLBACK: assert Go + modules, NOT "backend" —
    // a go.mod backs libraries, Gin/Echo servers, Cobra CLIs, and MCP servers.
    // turbo-cat.ts reads CONTENT (detectGoProject) and fills framework/backend.
    frameworks: ['Go'],
    slots: {
      packageManager: 'go modules',
      mainLanguage: 'Go',
      buildTool: 'go build'
    },
    priority: 36,
    intelligence: 'ultra-high'
  },

  'Package.swift': {
    // Filename-only FALLBACK: assert Swift + SPM, NOT app/iOS/Vapor —
    // Package.swift alone ≠ app (library · executable · plugin · server · MCP).
    // turbo-cat.ts reads CONTENT (detectSwiftProject) and fills appType signals.
    frameworks: ['Swift'],
    slots: {
      packageManager: 'spm',
      mainLanguage: 'Swift',
      buildTool: 'swift build'
    },
    priority: 36,
    intelligence: 'ultra-high'
  },

  // Note: real projects use Named.csproj — turbo-cat.ts handles any *.csproj
  // via content-aware detectCsharpProject. This key is documentation-only
  // fallback if a literal file named "*.csproj" ever appears (it won't).
  '*.csproj': {
    frameworks: ['C#', '.NET'],
    slots: {
      packageManager: 'NuGet',
      mainLanguage: 'C#',
      buildTool: 'dotnet'
    },
    priority: 36,
    intelligence: 'ultra-high'
  },
  
  'pubspec.yaml': {
    // Filename-only FALLBACK: assert Dart + pub, NOT Flutter — a pubspec backs
    // Flutter apps, pure-Dart CLIs/packages/servers, and MCP servers alike.
    // turbo-cat.ts reads the CONTENT (detectDartProject) and adds the framework
    // (Flutter / Dart Frog / …) when the deps actually prove it.
    frameworks: ['Dart'],
    slots: {
      packageManager: 'pub',
      mainLanguage: 'Dart'
    },
    priority: 35,
    intelligence: 'ultra-high'
  },
  
  'pom.xml': {
    // Filename-only FALLBACK: assert Java + Maven, NOT Spring —
    // a pom backs libraries, Boot apps, aggregators/BOMs, MCP servers.
    // turbo-cat.ts reads CONTENT (detectJvmProject) for appType/framework.
    frameworks: ['Java'],
    slots: {
      packageManager: 'maven',
      mainLanguage: 'Java',
      buildTool: 'maven'
    },
    priority: 35,
    intelligence: 'ultra-high'
  },

  'build.gradle': {
    // Filename-only FALLBACK: assert JVM + Gradle, NOT Spring/Android.
    // Content-aware detectJvmProject fills Spring / Android / KMP / MCP.
    frameworks: ['Java', 'Gradle'],
    slots: {
      packageManager: 'gradle',
      mainLanguage: 'Java',
      buildTool: 'gradle'
    },
    priority: 35,
    intelligence: 'ultra-high'
  },

  'settings.gradle': {
    frameworks: ['Java', 'Gradle'],
    slots: {
      packageManager: 'gradle',
      mainLanguage: 'Java',
      buildTool: 'gradle'
    },
    priority: 34,
    intelligence: 'high'
  },

  'settings.gradle.kts': {
    frameworks: ['Kotlin', 'Gradle'],
    slots: {
      packageManager: 'gradle',
      mainLanguage: 'Kotlin',
      buildTool: 'gradle'
    },
    priority: 34,
    intelligence: 'high'
  },

  // ============================================
  // HIGH VALUE (30 points) - JavaScript Frameworks
  // ============================================
  
  'next.config.js': {
    frameworks: ['Next.js'],
    slots: { 
      framework: 'Next.js',
      buildTool: 'Next.js',
      mainLanguage: 'JavaScript',
      server: 'Node.js'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'next.config.mjs': {
    frameworks: ['Next.js'],
    slots: { 
      framework: 'Next.js',
      buildTool: 'Next.js',
      mainLanguage: 'JavaScript',
      server: 'Node.js'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'next.config.ts': {
    frameworks: ['Next.js 13+'],
    slots: { 
      framework: 'Next.js 13+',
      buildTool: 'Next.js',
      mainLanguage: 'TypeScript',
      server: 'Node.js'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'nuxt.config.js': {
    frameworks: ['Nuxt 2'],
    slots: { 
      framework: 'Nuxt 2',
      buildTool: 'Nuxt',
      mainLanguage: 'JavaScript',
      server: 'Node.js'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'nuxt.config.ts': {
    frameworks: ['Nuxt 3'],
    slots: { 
      framework: 'Nuxt 3',
      buildTool: 'Nuxt',
      mainLanguage: 'TypeScript',
      server: 'Node.js'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'svelte.config.js': {
    frameworks: ['SvelteKit'],
    slots: { 
      framework: 'SvelteKit',
      buildTool: 'Vite',
      mainLanguage: 'JavaScript',
      server: 'Node.js'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'angular.json': {
    frameworks: ['Angular'],
    slots: {
      framework: 'Angular',
      buildTool: 'Angular CLI',
      mainLanguage: 'TypeScript',
      server: 'Node.js'
    },
    priority: 30,
    intelligence: 'high'
  },

  // ============================================
  // CHROME EXTENSION SUPPORT
  // ============================================

  'manifest.json': {
    frameworks: ['Chrome Extension', 'Browser Extension'],
    slots: {
      framework: 'Chrome Extension',
      runtime: 'Chrome/Browser',
      mainLanguage: 'JavaScript',
      hosting: 'Chrome Web Store',
      apiType: 'Chrome APIs'
    },
    priority: 35,
    intelligence: 'ultra-high',
    confirmWith: ['popup.html', 'background.js', 'content.js']
  },
  
  'vue.config.js': {
    frameworks: ['Vue'],
    slots: { 
      framework: 'Vue',
      buildTool: 'Vue CLI',
      mainLanguage: 'JavaScript'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'remix.config.js': {
    frameworks: ['Remix'],
    slots: { 
      framework: 'Remix',
      mainLanguage: 'JavaScript',
      server: 'Node.js'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'gatsby-config.js': {
    frameworks: ['Gatsby'],
    slots: { 
      framework: 'Gatsby',
      buildTool: 'Gatsby',
      mainLanguage: 'JavaScript',
      server: 'Node.js'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'gatsby-config.ts': {
    frameworks: ['Gatsby'],
    slots: { 
      framework: 'Gatsby',
      buildTool: 'Gatsby',
      mainLanguage: 'TypeScript',
      server: 'Node.js'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'astro.config.mjs': {
    frameworks: ['Astro'],
    slots: { 
      framework: 'Astro',
      buildTool: 'Vite',
      mainLanguage: 'JavaScript',
      server: 'Node.js'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'redwood.toml': {
    frameworks: ['RedwoodJS'],
    slots: { 
      framework: 'RedwoodJS',
      buildTool: 'Webpack',
      mainLanguage: 'JavaScript/TypeScript',
      server: 'Node.js',
      database: 'Prisma'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'blitz.config.ts': {
    frameworks: ['Blitz.js'],
    slots: { 
      framework: 'Blitz.js',
      buildTool: 'Next.js',
      mainLanguage: 'TypeScript',
      server: 'Node.js',
      database: 'Prisma'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'qwik.config.ts': {
    frameworks: ['Qwik'],
    slots: { 
      framework: 'Qwik',
      buildTool: 'Vite',
      mainLanguage: 'TypeScript',
      server: 'Node.js'
    },
    priority: 30,
    intelligence: 'high'
  },

  // ============================================
  // HIGH VALUE (30 points) - Backend Frameworks
  // ============================================
  
  'manage.py': {
    frameworks: ['Django'],
    slots: { 
      framework: 'Django',
      backend: 'Django',
      mainLanguage: 'Python',
      server: 'Django',
      apiType: 'Django Views'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'artisan': {
    frameworks: ['Laravel'],
    slots: { 
      framework: 'Laravel',
      backend: 'Laravel',
      mainLanguage: 'PHP',
      server: 'PHP',
      apiType: 'Laravel Routes'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'mix.exs': {
    frameworks: ['Phoenix', 'Elixir'],
    slots: { 
      framework: 'Phoenix',
      backend: 'Phoenix',
      mainLanguage: 'Elixir',
      server: 'BEAM'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'nest-cli.json': {
    frameworks: ['NestJS'],
    slots: { 
      framework: 'NestJS',
      backend: 'NestJS',
      mainLanguage: 'TypeScript',
      server: 'Node.js',
      apiType: 'REST API'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'strapi.config.js': {
    frameworks: ['Strapi'],
    slots: { 
      backend: 'Strapi',
      apiType: 'REST API',
      server: 'Node.js'
    },
    priority: 30,
    intelligence: 'high'
  },

  // ============================================
  // HIGH VALUE (30 points) - Infrastructure
  // ============================================
  
  // A Dockerfile shows a container build, not where the app runs: no hosting.
  'Dockerfile': {
    frameworks: ['Docker'],
    slots: {},
    priority: 30,
    intelligence: 'high'
  },
  
  'docker-compose.yml': {
    frameworks: ['Docker Compose'],
    slots: { 
      hosting: 'Docker'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'docker-compose.yaml': {
    frameworks: ['Docker Compose'],
    slots: { 
      hosting: 'Docker'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'kubernetes.yml': {
    frameworks: ['Kubernetes'],
    slots: { 
      hosting: 'Kubernetes'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'k8s.yaml': {
    frameworks: ['Kubernetes'],
    slots: { 
      hosting: 'Kubernetes'
    },
    priority: 30,
    intelligence: 'high'
  },

  // ============================================
  // HIGH VALUE (30 points) - Deployment
  // ============================================
  
  'vercel.json': {
    frameworks: ['Vercel'],
    slots: { 
      hosting: 'Vercel',
      cicd: 'Vercel'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'netlify.toml': {
    frameworks: ['Netlify'],
    slots: { 
      hosting: 'Netlify',
      cicd: 'Netlify'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'render.yaml': {
    frameworks: ['Render'],
    slots: { 
      hosting: 'Render',
      cicd: 'Render'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'fly.toml': {
    frameworks: ['Fly.io'],
    slots: { 
      hosting: 'Fly.io',
      cicd: 'Fly.io'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'railway.json': {
    frameworks: ['Railway'],
    slots: { 
      hosting: 'Railway',
      cicd: 'Railway'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'railway.toml': {
    frameworks: ['Railway'],
    slots: { 
      hosting: 'Railway',
      cicd: 'Railway'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'app.yaml': {
    frameworks: ['Google App Engine'],
    slots: { 
      hosting: 'Google Cloud',
      cicd: 'Google Cloud'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'Procfile': {
    frameworks: ['Heroku'],
    slots: { 
      hosting: 'Heroku',
      cicd: 'Heroku'
    },
    priority: 30,
    intelligence: 'high'
  },

  // ============================================
  // MEDIUM VALUE (25 points) - Build Tools
  // ============================================
  
  'vite.config.js': {
    frameworks: ['Vite'],
    slots: { 
      buildTool: 'Vite'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'vite.config.ts': {
    frameworks: ['Vite'],
    slots: { 
      buildTool: 'Vite',
      mainLanguage: 'TypeScript'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'webpack.config.js': {
    frameworks: ['Webpack'],
    slots: { 
      buildTool: 'Webpack'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'rollup.config.js': {
    frameworks: ['Rollup'],
    slots: { 
      buildTool: 'Rollup'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'esbuild.config.js': {
    frameworks: ['esbuild'],
    slots: { 
      buildTool: 'esbuild'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'parcel.config.js': {
    frameworks: ['Parcel'],
    slots: { 
      buildTool: 'Parcel'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'snowpack.config.js': {
    frameworks: ['Snowpack'],
    slots: { 
      buildTool: 'Snowpack'
    },
    priority: 25,
    intelligence: 'medium'
  },

  // ============================================
  // MEDIUM VALUE (25 points) - Language Config
  // ============================================
  
  'tsconfig.json': {
    frameworks: ['TypeScript'],
    slots: { 
      mainLanguage: 'TypeScript'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'jsconfig.json': {
    frameworks: ['JavaScript'],
    slots: { 
      mainLanguage: 'JavaScript'
    },
    priority: 20,
    intelligence: 'medium'
  },
  
  'babel.config.js': {
    frameworks: ['Babel'],
    slots: { 
      buildTool: 'Babel'
    },
    priority: 20,
    intelligence: 'medium'
  },
  
  '.babelrc': {
    frameworks: ['Babel'],
    slots: { 
      buildTool: 'Babel'
    },
    priority: 20,
    intelligence: 'medium'
  },

  // ============================================
  // MEDIUM VALUE (25 points) - Testing
  // ============================================
  
  'jest.config.js': {
    frameworks: ['Jest'],
    slots: { 
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'jest.config.ts': {
    frameworks: ['Jest'],
    slots: { 
      mainLanguage: 'TypeScript'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'vitest.config.ts': {
    frameworks: ['Vitest'],
    slots: { 
      mainLanguage: 'TypeScript'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'cypress.config.js': {
    frameworks: ['Cypress'],
    slots: { 
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'playwright.config.ts': {
    frameworks: ['Playwright'],
    slots: { 
      mainLanguage: 'TypeScript'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'karma.conf.js': {
    frameworks: ['Karma'],
    slots: { 
    },
    priority: 20,
    intelligence: 'medium'
  },
  
  'mocha.opts': {
    frameworks: ['Mocha'],
    slots: { 
    },
    priority: 20,
    intelligence: 'medium'
  },
  
  '.rspec': {
    frameworks: ['RSpec'],
    slots: { 
      mainLanguage: 'Ruby'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'pytest.ini': {
    frameworks: ['Pytest'],
    slots: { 
      mainLanguage: 'Python'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'phpunit.xml': {
    frameworks: ['PHPUnit'],
    slots: { 
      mainLanguage: 'PHP'
    },
    priority: 25,
    intelligence: 'medium'
  },

  // ============================================
  // MEDIUM VALUE (25 points) - Database/ORM
  // ============================================
  
  'schema.prisma': {
    frameworks: ['Prisma'],
    slots: { 
      database: 'Prisma ORM',
      connection: 'Prisma'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'drizzle.config.ts': {
    frameworks: ['Drizzle'],
    slots: { 
      database: 'Drizzle ORM',
      connection: 'Drizzle',
      mainLanguage: 'TypeScript'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'ormconfig.js': {
    frameworks: ['TypeORM'],
    slots: { 
      database: 'TypeORM',
      connection: 'TypeORM'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'knexfile.js': {
    frameworks: ['Knex.js'],
    slots: { 
      database: 'Knex.js',
      connection: 'Knex.js'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'sequelize.config.js': {
    frameworks: ['Sequelize'],
    slots: { 
      database: 'Sequelize',
      connection: 'Sequelize'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'alembic.ini': {
    frameworks: ['Alembic'],
    slots: { 
      database: 'Alembic',
      mainLanguage: 'Python'
    },
    priority: 25,
    intelligence: 'medium'
  },

  // ============================================
  // MEDIUM VALUE (25 points) - CI/CD
  // ============================================
  
  '.github/workflows': {
    frameworks: ['GitHub Actions'],
    slots: { 
      cicd: 'GitHub Actions'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  '.gitlab-ci.yml': {
    frameworks: ['GitLab CI'],
    slots: { 
      cicd: 'GitLab CI'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'Jenkinsfile': {
    frameworks: ['Jenkins'],
    slots: { 
      cicd: 'Jenkins'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  '.circleci/config.yml': {
    frameworks: ['CircleCI'],
    slots: { 
      cicd: 'CircleCI'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  '.travis.yml': {
    frameworks: ['Travis CI'],
    slots: { 
      cicd: 'Travis CI'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'azure-pipelines.yml': {
    frameworks: ['Azure DevOps'],
    slots: { 
      cicd: 'Azure DevOps'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'bitbucket-pipelines.yml': {
    frameworks: ['Bitbucket Pipelines'],
    slots: { 
      cicd: 'Bitbucket Pipelines'
    },
    priority: 25,
    intelligence: 'medium'
  },

  // ============================================
  // MEDIUM VALUE (20 points) - CSS/UI
  // ============================================
  
  'tailwind.config.js': {
    frameworks: ['Tailwind CSS'],
    slots: { 
      cssFramework: 'Tailwind CSS'
    },
    priority: 20,
    intelligence: 'medium'
  },
  
  'tailwind.config.ts': {
    frameworks: ['Tailwind CSS'],
    slots: { 
      cssFramework: 'Tailwind CSS',
      mainLanguage: 'TypeScript'
    },
    priority: 20,
    intelligence: 'medium'
  },
  
  'postcss.config.js': {
    frameworks: ['PostCSS'],
    slots: { 
      cssFramework: 'PostCSS'
    },
    priority: 20,
    intelligence: 'medium'
  },
  
  'uno.config.ts': {
    frameworks: ['UnoCSS'],
    slots: { 
      cssFramework: 'UnoCSS',
      mainLanguage: 'TypeScript'
    },
    priority: 20,
    intelligence: 'medium'
  },
  
  'styled-components.config.js': {
    frameworks: ['styled-components'],
    slots: { 
      cssFramework: 'styled-components',
      uiLibrary: 'styled-components'
    },
    priority: 20,
    intelligence: 'medium'
  },
  
  'emotion.config.js': {
    frameworks: ['Emotion'],
    slots: { 
      cssFramework: 'Emotion',
      uiLibrary: 'Emotion'
    },
    priority: 20,
    intelligence: 'medium'
  },

  // ============================================
  // MEDIUM VALUE (25 points) - Mobile
  // ============================================
  
  'metro.config.js': {
    frameworks: ['React Native'],
    slots: { 
      framework: 'React Native',
      mainLanguage: 'JavaScript'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'expo.config.js': {
    frameworks: ['Expo'],
    slots: { 
      framework: 'React Native',
      buildTool: 'Expo'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'app.json': {
    frameworks: ['React Native', 'Expo'],
    slots: { 
      framework: 'React Native'
    },
    priority: 20,
    intelligence: 'medium',
    confirmWith: ['expo']
  },
  
  'capacitor.config.ts': {
    frameworks: ['Capacitor'],
    slots: { 
      framework: 'Capacitor',
      mainLanguage: 'TypeScript'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'ionic.config.json': {
    frameworks: ['Ionic'],
    slots: { 
      framework: 'Ionic',
      uiLibrary: 'Ionic'
    },
    priority: 25,
    intelligence: 'medium'
  },

  // ============================================
  // MEDIUM VALUE (25 points) - Monorepo
  // ============================================
  
  'nx.json': {
    frameworks: ['Nx'],
    slots: { 
      buildTool: 'Nx'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'turbo.json': {
    frameworks: ['Turborepo'],
    slots: { 
      buildTool: 'Turborepo'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'lerna.json': {
    frameworks: ['Lerna'],
    slots: { 
      buildTool: 'Lerna'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'rush.json': {
    frameworks: ['Rush'],
    slots: { 
      buildTool: 'Rush'
    },
    priority: 25,
    intelligence: 'medium'
  },
  
  'pnpm-workspace.yaml': {
    frameworks: ['pnpm workspaces'],
    slots: { 
      packageManager: 'pnpm',
      buildTool: 'pnpm workspaces'
    },
    priority: 25,
    intelligence: 'medium'
  },

  // ============================================
  // LOW VALUE (15 points) - File Extensions
  // These are pattern-based and less definitive
  // ============================================
  
  '*.svelte': {
    frameworks: ['Svelte', 'SvelteKit'],
    slots: { 
      framework: 'Svelte'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.vue': {
    frameworks: ['Vue'],
    slots: { 
      framework: 'Vue'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.jsx': {
    frameworks: ['React'],
    slots: { 
      framework: 'React',
      mainLanguage: 'JavaScript'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.ts': {
    frameworks: ['TypeScript'],
    slots: {
      mainLanguage: 'TypeScript'
    },
    priority: 15,
    intelligence: 'low'
  },

  '*.tsx': {
    frameworks: ['React', 'TypeScript'],
    slots: {
      framework: 'React',
      mainLanguage: 'TypeScript'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.py': {
    frameworks: ['Python'],
    slots: { 
      mainLanguage: 'Python'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.rb': {
    frameworks: ['Ruby'],
    slots: { 
      mainLanguage: 'Ruby'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.php': {
    frameworks: ['PHP'],
    slots: { 
      mainLanguage: 'PHP'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.java': {
    frameworks: ['Java'],
    slots: { 
      mainLanguage: 'Java'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.kt': {
    frameworks: ['Kotlin'],
    slots: { 
      mainLanguage: 'Kotlin'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.swift': {
    frameworks: ['Swift'],
    slots: { 
      mainLanguage: 'Swift'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.go': {
    frameworks: ['Go'],
    slots: { 
      mainLanguage: 'Go'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.rs': {
    frameworks: ['Rust'],
    slots: { 
      mainLanguage: 'Rust'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.dart': {
    frameworks: ['Flutter', 'Dart'],
    slots: { 
      mainLanguage: 'Dart'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.ex': {
    frameworks: ['Elixir'],
    slots: { 
      mainLanguage: 'Elixir'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.exs': {
    frameworks: ['Elixir'],
    slots: { 
      mainLanguage: 'Elixir'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.scala': {
    frameworks: ['Scala'],
    slots: { 
      mainLanguage: 'Scala'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.clj': {
    frameworks: ['Clojure'],
    slots: { 
      mainLanguage: 'Clojure'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.lua': {
    frameworks: ['Lua'],
    slots: { 
      mainLanguage: 'Lua'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.r': {
    frameworks: ['R'],
    slots: { 
      mainLanguage: 'R'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.R': {
    frameworks: ['R'],
    slots: { 
      mainLanguage: 'R'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.jl': {
    frameworks: ['Julia'],
    slots: { 
      mainLanguage: 'Julia'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.nim': {
    frameworks: ['Nim'],
    slots: { 
      mainLanguage: 'Nim'
    },
    priority: 15,
    intelligence: 'low'
  },
  
  '*.zig': {
    frameworks: ['Zig'],
    slots: {
      mainLanguage: 'Zig'
    },
    priority: 15,
    intelligence: 'low'
  },

  'build.zig': {
    frameworks: ['Zig'],
    slots: {
      // camelCase slots (standard TURBO-CAT)
      packageManager: 'Zig Package Manager',
      mainLanguage: 'Zig',
      buildTool: 'zig build',
      // snake_case slots (template compatibility)
      build: 'zig build'
    },
    priority: 35,
    intelligence: 'ultra-high'
  },

  // ============================================
  // SPECIAL VALUE (20 points) - API/Schema
  // ============================================
  
  'openapi.json': {
    frameworks: ['OpenAPI'],
    slots: { 
      apiType: 'REST API'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'openapi.yaml': {
    frameworks: ['OpenAPI'],
    slots: { 
      apiType: 'REST API'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'swagger.json': {
    frameworks: ['Swagger'],
    slots: { 
      apiType: 'REST API'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'graphql.schema': {
    frameworks: ['GraphQL'],
    slots: { 
      apiType: 'GraphQL'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  'schema.graphql': {
    frameworks: ['GraphQL'],
    slots: { 
      apiType: 'GraphQL'
    },
    priority: 30,
    intelligence: 'high'
  },
  
  '.graphqlconfig': {
    frameworks: ['GraphQL'],
    slots: { 
      apiType: 'GraphQL'
    },
    priority: 25,
    intelligence: 'medium'
  },

  // ============================================
  // SPECIAL VALUE (20 points) - State Management
  // ============================================
  
  'redux.config.js': {
    frameworks: ['Redux'],
    slots: { 
      stateManagement: 'Redux'
    },
    priority: 20,
    intelligence: 'medium'
  },
  
  'mobx.config.js': {
    frameworks: ['MobX'],
    slots: { 
      stateManagement: 'MobX'
    },
    priority: 20,
    intelligence: 'medium'
  },
  
  'vuex.config.js': {
    frameworks: ['Vuex'],
    slots: { 
      stateManagement: 'Vuex',
      framework: 'Vue'
    },
    priority: 20,
    intelligence: 'medium'
  },
  
  'pinia.config.js': {
    frameworks: ['Pinia'],
    slots: { 
      stateManagement: 'Pinia',
      framework: 'Vue'
    },
    priority: 20,
    intelligence: 'medium'
  },
  
  'zustand.config.js': {
    frameworks: ['Zustand'],
    slots: { 
      stateManagement: 'Zustand'
    },
    priority: 20,
    intelligence: 'medium'
  },

  // ============================================
  // SPECIAL VALUE (15 points) - Linting/Formatting
  // ============================================
  
  '.eslintrc.js': {
    frameworks: ['ESLint'],
    slots: {},
    priority: 15,
    intelligence: 'low'
  },
  
  '.eslintrc.json': {
    frameworks: ['ESLint'],
    slots: {},
    priority: 15,
    intelligence: 'low'
  },
  
  '.prettierrc': {
    frameworks: ['Prettier'],
    slots: {},
    priority: 15,
    intelligence: 'low'
  },
  
  '.prettierrc.json': {
    frameworks: ['Prettier'],
    slots: {},
    priority: 15,
    intelligence: 'low'
  },
  
  'biome.json': {
    frameworks: ['Biome'],
    slots: {},
    priority: 15,
    intelligence: 'low'
  },
  
  '.editorconfig': {
    frameworks: ['EditorConfig'],
    slots: {},
    priority: 10,
    intelligence: 'low'
  },

  // ============================================
  // HIGH VALUE (30 points) - Serverless
  // ============================================

  'serverless.yml': {
    frameworks: ['Serverless Framework'],
    slots: {
      hosting: 'AWS Lambda/Azure/GCP',
      backend: 'Serverless',
      apiType: 'API Gateway'
    },
    priority: 30,
    intelligence: 'high'
  },

  'serverless.yaml': {
    frameworks: ['Serverless Framework'],
    slots: {
      hosting: 'AWS Lambda/Azure/GCP',
      backend: 'Serverless',
      apiType: 'API Gateway'
    },
    priority: 30,
    intelligence: 'high'
  },

  'sam.yaml': {
    frameworks: ['AWS SAM'],
    slots: {
      hosting: 'AWS Lambda',
      backend: 'AWS SAM',
      cicd: 'AWS SAM CLI'
    },
    priority: 30,
    intelligence: 'high'
  },

  'template.yaml': {
    frameworks: ['AWS SAM', 'CloudFormation'],
    slots: {
      hosting: 'AWS',
      backend: 'AWS SAM/CloudFormation',
      cicd: 'AWS'
    },
    priority: 25,
    intelligence: 'medium',
    confirmWith: ['samconfig.toml']
  },

  'amplify.yml': {
    frameworks: ['AWS Amplify'],
    slots: {
      hosting: 'AWS Amplify',
      backend: 'AWS Amplify',
      cicd: 'AWS Amplify'
    },
    priority: 30,
    intelligence: 'high'
  },

  // ============================================
  // HIGH VALUE (30 points) - Mobile Native
  // ============================================

  'Podfile': {
    // Softened 2026-08-03 (Swift Edition honesty): CocoaPods is multi-platform
    // (iOS / macOS / tvOS / watchOS) — not iOS-only. Do not stamp appType here.
    frameworks: ['CocoaPods', 'Swift'],
    slots: {
      packageManager: 'CocoaPods',
      mainLanguage: 'Swift/Objective-C'
    },
    priority: 30,
    intelligence: 'high'
  },

  'build.gradle.kts': {
    // Filename-only FALLBACK: Kotlin DSL ≠ Android. Content-aware detectJvmProject.
    frameworks: ['Kotlin', 'Gradle'],
    slots: {
      packageManager: 'gradle',
      mainLanguage: 'Kotlin',
      buildTool: 'gradle'
    },
    priority: 35,
    intelligence: 'ultra-high'
  },

  // ============================================
  // HIGH VALUE (30 points) - AI/ML
  // ============================================

  '*.ipynb': {
    frameworks: ['Jupyter', 'Python'],
    slots: {
      mainLanguage: 'Python',
      framework: 'Jupyter Notebook'
    },
    priority: 30,
    intelligence: 'high'
  },

  'MLproject': {
    frameworks: ['MLflow'],
    slots: {
      mainLanguage: 'Python',
      backend: 'MLflow'
    },
    priority: 30,
    intelligence: 'high'
  },

  'dvc.yaml': {
    frameworks: ['DVC', 'Data Version Control'],
    slots: {
      mainLanguage: 'Python',
      cicd: 'DVC Pipelines'
    },
    priority: 30,
    intelligence: 'high'
  },

  'dvc.lock': {
    frameworks: ['DVC'],
    slots: {
    },
    priority: 20,
    intelligence: 'medium'
  },

  // ============================================
  // ROW 18 - BEYOND THE SACRED 154 (Target: 172)
  // ============================================

  // Edge Computing
  'wrangler.toml': {
    frameworks: ['Cloudflare Workers', 'Cloudflare'],
    slots: {
      hosting: 'Cloudflare Workers',
      backend: 'Cloudflare Workers',
      runtime: 'V8 Isolates'
    },
    priority: 30,
    intelligence: 'high'
  },

  // Modern Runtimes
  'deno.json': {
    frameworks: ['Deno'],
    slots: {
      runtime: 'Deno',
      mainLanguage: 'TypeScript',
      packageManager: 'Deno'
    },
    priority: 30,
    intelligence: 'high'
  },

  'bun.lockb': {
    frameworks: ['Bun'],
    slots: {
      runtime: 'Bun',
      packageManager: 'Bun',
      mainLanguage: 'TypeScript/JavaScript'
    },
    priority: 30,
    intelligence: 'high'
  },

  // CI/CD Ecosystem
  'codecov.yml': {
    frameworks: ['Codecov'],
    slots: {
    },
    priority: 25,
    intelligence: 'high'
  },

  'dependabot.yml': {
    frameworks: ['Dependabot', 'GitHub'],
    slots: {
    },
    priority: 25,
    intelligence: 'high'
  },

  'renovate.json': {
    frameworks: ['Renovate'],
    slots: {
    },
    priority: 25,
    intelligence: 'high'
  },

  // Universal Build Tools
  'Makefile': {
    frameworks: ['Make', 'GNU Make'],
    slots: {
      buildTool: 'Make'
    },
    priority: 30,
    intelligence: 'high'
  },

  'CMakeLists.txt': {
    frameworks: ['CMake', 'C', 'C++'],
    slots: {
      buildTool: 'CMake',
      mainLanguage: 'C/C++'
    },
    priority: 30,
    intelligence: 'high'
  },

  'meson.build': {
    frameworks: ['Meson', 'C', 'C++'],
    slots: {
      buildTool: 'Meson',
      mainLanguage: 'C/C++'
    },
    priority: 30,
    intelligence: 'high'
  },

  // ============================================
  // C/C++ LANGUAGE FAMILY - SYSTEMS PROGRAMMING
  // ============================================

  '*.c': {
    frameworks: ['C'],
    slots: {
      mainLanguage: 'C'
    },
    priority: 30,
    intelligence: 'high'
  },

  '*.cpp': {
    frameworks: ['C++'],
    slots: {
      mainLanguage: 'C++'
    },
    priority: 30,
    intelligence: 'high'
  },

  '*.cc': {
    frameworks: ['C++'],
    slots: {
      mainLanguage: 'C++'
    },
    priority: 30,
    intelligence: 'high'
  },

  '*.cxx': {
    frameworks: ['C++'],
    slots: {
      mainLanguage: 'C++'
    },
    priority: 25,
    intelligence: 'high'
  },

  '*.h': {
    frameworks: ['C', 'C++'],
    slots: {
      mainLanguage: 'C/C++'
    },
    priority: 25,
    intelligence: 'high'
  },

  '*.hpp': {
    frameworks: ['C++'],
    slots: {
      mainLanguage: 'C++'
    },
    priority: 25,
    intelligence: 'high'
  },

  '*.hxx': {
    frameworks: ['C++'],
    slots: {
      mainLanguage: 'C++'
    },
    priority: 20,
    intelligence: 'medium'
  },

  // ============================================
  // ENVIRONMENT FILES - CONFIGURATION
  // ============================================

  '.env': {
    frameworks: [],
    slots: {},
    priority: 20,
    intelligence: 'medium'
  },

  '.env.example': {
    frameworks: [],
    slots: {},
    priority: 25,
    intelligence: 'high'
  },

  '.env.local': {
    frameworks: [],
    slots: {},
    priority: 20,
    intelligence: 'medium'
  },

  '.env.development': {
    frameworks: [],
    slots: {},
    priority: 20,
    intelligence: 'medium'
  },

  '.env.production': {
    frameworks: [],
    slots: {},
    priority: 20,
    intelligence: 'medium'
  },

  // ============================================
  // DEVOPS YAML - KUBERNETES & IaC
  // ============================================

  'skaffold.yaml': {
    frameworks: ['Skaffold', 'Kubernetes'],
    slots: {
      hosting: 'Kubernetes',
      cicd: 'Skaffold'
    },
    priority: 30,
    intelligence: 'high'
  },

  'Chart.yaml': {
    frameworks: ['Helm', 'Kubernetes'],
    slots: {
      hosting: 'Kubernetes'
    },
    priority: 30,
    intelligence: 'high'
  },

  'values.yaml': {
    frameworks: ['Helm', 'Kubernetes'],
    slots: {
      hosting: 'Kubernetes'
    },
    priority: 25,
    intelligence: 'high',
    confirmWith: ['Chart.yaml']
  },

  'pulumi.yaml': {
    frameworks: ['Pulumi'],
    slots: {
      cicd: 'Pulumi'
    },
    priority: 30,
    intelligence: 'high'
  },

  'Pulumi.yaml': {
    frameworks: ['Pulumi'],
    slots: {
      cicd: 'Pulumi'
    },
    priority: 30,
    intelligence: 'high'
  },

  'terragrunt.hcl': {
    frameworks: ['Terragrunt', 'Terraform'],
    slots: {
      cicd: 'Terragrunt'
    },
    priority: 30,
    intelligence: 'high'
  },

  'ansible.yml': {
    frameworks: ['Ansible'],
    slots: {
      cicd: 'Ansible'
    },
    priority: 30,
    intelligence: 'high'
  },

  'playbook.yml': {
    frameworks: ['Ansible'],
    slots: {
      cicd: 'Ansible'
    },
    priority: 25,
    intelligence: 'high'
  },

  // ============================================
  // ROW 19 - LANGUAGES & VERSION MANAGERS
  // ============================================

  '*.cs': {
    frameworks: ['C#', '.NET', 'Unity'],
    slots: {
      mainLanguage: 'C#'
    },
    priority: 30,
    intelligence: 'high'
  },

  '*.m': {
    frameworks: ['Objective-C', 'iOS'],
    slots: {
      mainLanguage: 'Objective-C'
    },
    priority: 25,
    intelligence: 'high'
  },

  '.nvmrc': {
    frameworks: ['Node.js', 'nvm'],
    slots: {
      runtime: 'Node.js'
    },
    priority: 25,
    intelligence: 'high'
  },

  '.tool-versions': {
    frameworks: ['asdf'],
    slots: {},
    priority: 25,
    intelligence: 'high'
  },

  '.ruby-version': {
    frameworks: ['Ruby', 'rbenv'],
    slots: {
      mainLanguage: 'Ruby',
      runtime: 'Ruby'
    },
    priority: 25,
    intelligence: 'high'
  },

  '.python-version': {
    frameworks: ['Python', 'pyenv'],
    slots: {
      mainLanguage: 'Python',
      runtime: 'Python'
    },
    priority: 25,
    intelligence: 'high'
  },

  '.dockerignore': {
    frameworks: ['Docker'],
    slots: {},
    priority: 20,
    intelligence: 'medium'
  },

  '.gitattributes': {
    frameworks: ['Git'],
    slots: {},
    priority: 15,
    intelligence: 'low'
  },

  '.npmrc': {
    frameworks: ['npm', 'Node.js'],
    slots: {
      packageManager: 'npm'
    },
    priority: 20,
    intelligence: 'medium'
  },

  'sonar-project.properties': {
    frameworks: ['SonarQube', 'SonarCloud'],
    slots: {
    },
    priority: 25,
    intelligence: 'high'
  },

  // ROW 19 COMPLETION - FUNCTIONAL LANGUAGES

  '*.fs': {
    frameworks: ['F#', '.NET'],
    slots: {
      mainLanguage: 'F#'
    },
    priority: 25,
    intelligence: 'high'
  },

  '*.mm': {
    frameworks: ['Objective-C++', 'iOS', 'macOS'],
    slots: {
      mainLanguage: 'Objective-C++'
    },
    priority: 25,
    intelligence: 'high'
  },

  // ============================================
  // ROW 20 - TIER 1 EMERGING TECH (2025)
  // Target: 211 formats (Sum(1..20) + 1)
  // ============================================

  // Zig - The Next Systems Language
  'build.zig.zon': {
    frameworks: ['Zig'],
    slots: {
      mainLanguage: 'Zig',
      packageManager: 'Zig Package Manager',
      buildTool: 'zig build'
    },
    priority: 35,
    intelligence: 'ultra-high'
  },

  // Gleam - 2nd Most Admired Language 2025 (Stack Overflow)
  'gleam.toml': {
    frameworks: ['Gleam', 'BEAM', 'Erlang'],
    slots: {
      mainLanguage: 'Gleam',
      runtime: 'BEAM',
      packageManager: 'Gleam'
    },
    priority: 30,
    intelligence: 'high'
  },

  // Bun - Ultra-fast JS Runtime Config
  'bunfig.toml': {
    frameworks: ['Bun'],
    slots: {
      runtime: 'Bun',
      packageManager: 'Bun',
      buildTool: 'Bun'
    },
    priority: 30,
    intelligence: 'high'
  },

  // mise - Modern Version Manager (replaced asdf/rtx)
  'mise.toml': {
    frameworks: ['mise'],
    slots: {},
    priority: 25,
    intelligence: 'high'
  },

  '.mise.toml': {
    frameworks: ['mise'],
    slots: {},
    priority: 25,
    intelligence: 'high'
  },

  // Flox - Nix-based Development Environments
  'manifest.toml': {
    frameworks: ['Flox', 'Nix'],
    slots: {},
    priority: 25,
    intelligence: 'high',
    confirmWith: ['.flox']
  },

  // Just - Command Runner (10k+ GitHub stars, Rust ecosystem)
  'justfile': {
    frameworks: ['Just'],
    slots: {
      buildTool: 'Just'
    },
    priority: 25,
    intelligence: 'high'
  },

  // Pre-commit - Git Hooks Framework (industry standard)
  '.pre-commit-config.yaml': {
    frameworks: ['pre-commit'],
    slots: {
    },
    priority: 25,
    intelligence: 'high'
  },

  // Claude Code - AI Context (Anthropic Claude Code projects)
  'CLAUDE.md': {
    frameworks: ['Claude Code'],
    slots: {},
    priority: 30,  // High priority - AI context is valuable
    intelligence: 'high',
    confirmWith: ['.claude']
  }
};

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get knowledge for a specific format
 */
export function getFormatKnowledge(format: string): FormatKnowledge | undefined {
  return KNOWLEDGE_BASE[format];
}

/**
 * Get all format keys
 */
export function getAllFormats(): string[] {
  return Object.keys(KNOWLEDGE_BASE);
}

/**
 * Get high-value formats (priority >= 30)
 */
export function getHighValueFormats(): string[] {
  return Object.entries(KNOWLEDGE_BASE)
    .filter(([, knowledge]) => knowledge.priority >= 30)
    .map(([format]) => format);
}

/**
 * Get formats by intelligence level
 */
export function getFormatsByIntelligence(level: FormatKnowledge['intelligence']): string[] {
  return Object.entries(KNOWLEDGE_BASE)
    .filter(([, knowledge]) => knowledge.intelligence === level)
    .map(([format]) => format);
}

/**
 * Get formats that can fill a specific slot
 */
export function getFormatsForSlot(slotName: keyof ContextSlots): string[] {
  return Object.entries(KNOWLEDGE_BASE)
    .filter(([, knowledge]) => slotName in knowledge.slots)
    .map(([format]) => format);
}

/**
 * Calculate total possible intelligence score
 */
export function getMaxIntelligenceScore(): number {
  return Object.values(KNOWLEDGE_BASE)
    .reduce((sum, knowledge) => sum + knowledge.priority, 0);
}